/**
 * ProtectedRoute Component
 * Route guard that checks authentication and redirects if needed
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '@/hooks/redux';
import { FullPageLoader } from '@/components/common/Loader';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  redirectTo?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = true,
  redirectTo = '/login',
}) => {
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAppSelector((state) => state.auth);

  // Show loader while checking authentication
  if (isLoading) {
    return <FullPageLoader text="Checking authentication..." />;
  }

  // Redirect if authentication is required but user is not authenticated
  if (requireAuth && !isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Redirect if user is authenticated but shouldn't be on this route (e.g., login page)
  if (!requireAuth && isAuthenticated) {
    return <Navigate to="/entity-type" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
